import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from '@headlessui/react';
import { UserIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { useState } from 'react';
import { Textarea } from '../../../ui/form/Textarea';
import timeawayApi from '../../../network/timeaway';

interface RequestChangesModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  user: {
    id: string;
    full_name: string;
  };
  onRequestChangesSuccess?: () => void;
}

export default function RequestChangesModal({
  open,
  setOpen,
  user,
  onRequestChangesSuccess,
}: RequestChangesModalProps) {
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleRequestChanges = async () => {
    if (!user.id) {
      alert('Error: Timeaway request ID is missing.');
      return;
    }
    try {
      setIsSubmitting(true);
      await timeawayApi.requestChangesTimeawayRequest(
        user.id,
        reason.trim() || undefined,
      );
      setOpen(false);
      setReason('');
      onRequestChangesSuccess?.();
    } catch (error: any) {
      console.error('Failed to request changes:', error);
      alert(
        `Failed to request changes. ${
          error.response?.data?.message || error.message
        }`,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={setOpen} className="relative z-10">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4 text-center ">
          <DialogPanel
            transition
            className="relative w-full max-w-lg transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in "
          >
            <div className="bg-white px-6  pt-2">
              <div className="flex flex-col">
                <div className="mt-4 flex justify-between">
                  <DialogTitle as="h6" className="font-semibold text-gray-900">
                    Request changes
                  </DialogTitle>
                  <button onClick={() => setOpen(false)} aria-label="Close dialog">
                    <XMarkIcon className="w-4" />
                  </button>
                </div>
                <div className="mt-3">
                  <p className="text-sm text-gray-600">
                    The employee will receive a notification to update their request.
                  </p>
                </div>
                <div className="mt-2 flex flex-row gap-2 rounded-lg bg-[#FAFAFA] px-4 py-2">
                  <UserIcon className="w-4" />
                  {user.full_name}
                </div>
                <div className="mt-3 flex flex-col gap-1">
                  <p className="font-semibold">Comment (optional)</p>
                  <Textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="What should the employee change in this request?"
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-row justify-end gap-3 px-6 py-5">
              <button
                onClick={() => setOpen(false)}
                className="px-4 py-1"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                onClick={handleRequestChanges}
                className="rounded-lg bg-[#D97706] px-4 py-1 text-white"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Sending...' : 'Send request'}
              </button>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}

